<script>
import {
  coursesExpressProductListEmits,
  coursesExpressProductListProps,
  useCoursesExpressProductList
} from "./mixin";
import ProductCategory from "@/components/organisms/product-category";
import FormCheckbox from "@/components/molecules/form-checkbox";
import StickyFooter from "@/components/molecules/sticky-footer";
import Button from "@/components/atoms/button";
import Card from "@/components/molecules/card";
import GridRow from "@/components/molecules/grid/grid-row";
import GridCol from "@/components/molecules/grid/grid-col";
import Titre from "@/components/atoms/titre";
import ItemContainer from "@/containers/panier/item";
import FormSwitch from "@/components/molecules/form-switch";
import ProductListItemGrid from "@/components/organisms/product-list/product-list-item-grid/default";
import { useTuileSimplifie } from "@/composables/useTuileSimplifie";

export default {
  name: "CoursesExpressProductList",
  components: {
    FormCheckbox,
    StickyFooter,
    Button,
    ProductCategory,
    Card,
    Titre,
    GridRow,
    GridCol,
    FormSwitch,
    ItemContainer
  },
  props: {
    modeAffichageInitial: {
      type: String,
      require: false,
      default: "list"
    },
    ...coursesExpressProductListProps()
  },
  emits: ["delete", "addToCart", ...coursesExpressProductListEmits()],
  setup(props, context) {
    const modeAffichage = ref(props.modeAffichageInitial);
    const {
      itemsBySections,
      sectionIndisponible,
      totalItems,
      toggleSelectProduct,
      isSelected,
      selectAll,
      unselectAll,
      deleteItem,
      selectedProducts
    } = useCoursesExpressProductList(props, context);

    const { isSimplifie, setSimplifie } = useTuileSimplifie();

    return {
      itemsBySections,
      sectionIndisponible,
      totalItems,
      isSimplifie,
      setSimplifie,
      toggleSelectProduct,
      isSelected,
      selectAll,
      unselectAll,
      deleteItem,
      selectedProducts,
      ProductListItemGrid,
      modeAffichage
    };
  }
};
</script>

<template>
  <div
    id="prd-list"
    class="c-courses-express-product-list u-text-center"
  >
    <div class="u-flex u-flex-justify-end u-full-width u-pr-md u-pb-md">
      <FormSwitch
        :value="isSimplifie"
        class="u-text-right u-font-13"
        @change="setSimplifie"
      >
        Afficher moins <br />
        d'informations
      </FormSwitch>
    </div>
    <client-only>
      <Card
        radius="md"
        class="u-p-md"
      >
        <ProductCategory
          v-for="section in itemsBySections"
          :key="section.valeur"
          grid
          class="u-mb-lg"
        >
          <template #title>
            <Titre
              tag="h2"
              size="sm"
            >
              {{ section.libelle }} <span class="u-font-15">({{ section.articles.length }} {{ pluralize(section.articles.length, 'produit') }})</span>
            </Titre>
          </template>
          <li
            v-for="(item,index) in section.articles"
            :id="`prd-list-${item.attributes.produit.data.id}`"
            :key="item.id"
            class="c-courses-express-product-list__item u-mt-md"
            :class="isSelected(item.id) && 'c-courses-express-product-list__item--selected'"
          >
            <div class="u-flex u-flex-column u-full-height u-flex-justify-start">
              <ItemContainer
                :component="ProductListItemGrid"
                :is-simplifie="isSimplifie"
                :cart-context="cartContext"
                :can-delete="isSelected(item.id)"
                :index="index"
                :article="item.attributes.produit.data"
                @delete="deleteItem(item)"
              />
              <FormCheckbox
                :value="isSelected(item.id)"
                :class="isSelected(item.id) ? 'u-bgcolor-primary-50 c-courses-express-product-list__check-outline' : ''"
                class="u-pt-sm u-pb-xs25 u-mt-xxs"
                @change="toggleSelectProduct($event, item)"
              />
            </div>
          </li>
        </ProductCategory>

        <ProductCategory
          v-if="sectionIndisponible.length > 0"
          grid
          class="u-mb-lg"
        >
          <template #title>
            <Titre
              tag="h2"
              size="sm"
            >
              Indisponibles <span class="u-font-15">({{ sectionIndisponible.length }} {{ pluralize(sectionIndisponible.length, 'produit') }})</span>
            </Titre>
          </template>
          <li
            v-for="item in sectionIndisponible"
            :key="item.id"
            class="c-courses-express-product-list__item u-mt-md"
            :class="isSelected(item.id) && 'c-courses-express-product-list__item--selected'"
          >
            <div class="u-flex u-flex-column u-full-height u-flex-justify-start">
              <ItemContainer
                :component="ProductListItemGrid"
                :is-simplifie="isSimplifie"
                :article="item.attributes.produit.data"
                indisponible
                :can-delete="isSelected(item.id)"
                @delete="deleteItem(item)"
              />
              <FormCheckbox
                :value="isSelected(item.id)"
                :class="isSelected(item.id) ? 'u-bgcolor-primary-50 c-courses-express-product-list__check-outline' : ''"
                class="u-pt-sm u-pb-xs25 u-mt-xxs"
                @change="toggleSelectProduct($event, item)"
              />
            </div>
          </li>
        </ProductCategory>
      </Card>
    </client-only>

    <StickyFooter v-if="selectedProducts.length > 0">
      <GridRow class="u-flex-align-center">
        <GridCol
          cols="9"
          offset="3"
          class="u-flex u-flex-align-center u-flex-justify-between"
        >
          <template v-if="selectedProducts.length === totalItems">
            <Button
              link
              size="sm"
              @click="unselectAll"
            >
              Tout désélectionner
            </Button>
          </template>
          <template v-else>
            <Button
              link
              size="sm"
              @click="selectAll"
            >
              Tout sélectionner
            </Button>
          </template>
          <Button
            v-if="canDelete"
            variant="primary"
            radius="full"
            size="md"
            outline
            class="u-mr-md"
            @click="$emit('delete', selectedProducts)"
          >
            Supprimer les produits sélectionnés
          </Button>
          <Button
            variant="secondary"
            radius="full"
            size="md"
            @click="$emit('addToCart', selectedProducts)"
          >
            Ajouter au panier les produits sélectionnés
          </Button>
        </GridCol>
      </GridRow>
    </StickyFooter>
  </div>
</template>

<style lang="scss" scoped>
.c-courses-express-product-list__switch {
  border: 1px solid color(grey-4);
  border-radius: radius(md);
  overflow: hidden;
}
.c-courses-express-product-list__switch-button {
  height: 3.6rem;
  width: 3.6rem;
}

.c-product-category__list--line {
  .c-courses-express-product-list__item {
    margin: spacing(lg) 0 spacing(md) 0;
  }
}

.c-courses-express-product-list__check-outline {
  outline: 2px solid color(primary-50);
}

.c-courses-express-product-list__border-bottom {
  padding-bottom: 0.8rem;
  border-bottom: 1px dotted color(grey-5);
  margin-bottom: 2.4rem;
}
.c-courses-express-product-list__item--selected {
  z-index: zIndex(above);
}
</style>
